// src/modules/purityAnalyzer/components/hud/CalibrationCurve.jsx
// HUD plot of the ACTIVE calibration curve (density → purity) with the live
// sample point pulsing on it. Pure SVG, no chart library.

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const W = 320;
const H = 170;
const PAD = { l: 34, r: 12, t: 12, b: 24 };

export default function CalibrationCurve({ points = [], sample }) {
  const model = useMemo(() => {
    const pts = (points || [])
      .filter((p) => Number.isFinite(p.density) && Number.isFinite(p.purity))
      .sort((a, b) => a.density - b.density);
    if (pts.length < 2) return null;

    const xs = pts.map((p) => p.density);
    const ys = pts.map((p) => p.purity);
    const xMin = Math.min(...xs, sample?.density ?? Infinity) - 0.4;
    const xMax = Math.max(...xs, sample?.density ?? -Infinity) + 0.4;
    const yMin = Math.min(...ys, sample?.purity ?? Infinity) - 3;
    const yMax = Math.max(...ys, sample?.purity ?? -Infinity) + 3;

    const X = (d) => PAD.l + ((d - xMin) / (xMax - xMin)) * (W - PAD.l - PAD.r);
    const Y = (p) => H - PAD.b - ((p - yMin) / (yMax - yMin)) * (H - PAD.t - PAD.b);

    return {
      pts, X, Y, xMin, xMax, yMin, yMax,
      path: pts.map((p, i) => `${i ? 'L' : 'M'}${X(p.density).toFixed(1)},${Y(p.purity).toFixed(1)}`).join(' '),
    };
  }, [points, sample]);

  if (!model) return null;
  const { pts, X, Y, path } = model;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* Axis frame */}
      <rect x={PAD.l} y={PAD.t} width={W - PAD.l - PAD.r} height={H - PAD.t - PAD.b}
        fill="none" stroke="rgba(0,229,255,0.15)" strokeDasharray="3 3" />
      {/* Axis labels */}
      <text x={PAD.l - 6} y={PAD.t + 8} textAnchor="end" fill="rgba(148,197,222,0.6)"
        fontSize="8" fontFamily="monospace">{model.yMax.toFixed(0)}%</text>
      <text x={PAD.l - 6} y={H - PAD.b} textAnchor="end" fill="rgba(148,197,222,0.6)"
        fontSize="8" fontFamily="monospace">{model.yMin.toFixed(0)}%</text>
      <text x={PAD.l} y={H - 8} fill="rgba(148,197,222,0.6)" fontSize="8"
        fontFamily="monospace">{model.xMin.toFixed(1)}</text>
      <text x={W - PAD.r} y={H - 8} textAnchor="end" fill="rgba(148,197,222,0.6)"
        fontSize="8" fontFamily="monospace">{model.xMax.toFixed(1)} g/cm³</text>

      {/* Calibration curve */}
      <motion.path
        d={path} fill="none" stroke="#ffd166" strokeWidth="1.6"
        style={{ filter: 'drop-shadow(0 0 3px rgba(255,209,102,0.7))' }}
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      {/* Calibration points */}
      {pts.map((p) => (
        <circle key={`${p.density}-${p.purity}`} cx={X(p.density)} cy={Y(p.purity)} r="2.4"
          fill="#0a1020" stroke="#ffd166" strokeWidth="1.2" />
      ))}

      {/* Sample crosshair */}
      {sample && Number.isFinite(sample.density) && Number.isFinite(sample.purity) && (
        <g>
          <line x1={X(sample.density)} y1={PAD.t} x2={X(sample.density)} y2={H - PAD.b}
            stroke="rgba(0,229,255,0.35)" strokeDasharray="2 3" />
          <line x1={PAD.l} y1={Y(sample.purity)} x2={W - PAD.r} y2={Y(sample.purity)}
            stroke="rgba(0,229,255,0.35)" strokeDasharray="2 3" />
          <motion.circle
            cx={X(sample.density)} cy={Y(sample.purity)} fill="#00e5ff"
            style={{ filter: 'drop-shadow(0 0 6px #00e5ff)' }}
            animate={{ r: [3, 5.5, 3], opacity: [1, 0.65, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </g>
      )}
    </svg>
  );
}
